/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION: GA4 `purchase` event (W-23789028 / GTM data layer, slice 3).
 *
 * WHY THIS EXISTS. `docs/ASSESSMENT-GOOGLE-TAG-MANAGER.md` flags the purchase/transaction
 * event as "Critical missing" for the SF Next GTM parity: without it there is no revenue,
 * conversion, or ROAS reporting in GA4/Google Ads/Meta. The order-confirmation route is the
 * single place a completed transaction is known, so the producer lives there and the adapter
 * maps the SCAPI `Order` to the GA4 `purchase` ecommerce shape (transaction_id + tax +
 * shipping + coupon + line-item discounts).
 *
 * HOW THE EVENT TYPE IS ADDED. The runtime's `AnalyticsEvent` union is closed, but the runtime
 * exposes `AnalyticsEventExtensions` for exactly this — module augmentation adds `purchase`
 * to the union (and therefore to `EventTypeMap`, `eventToggles`, and the adapter's `switch`)
 * without forking the `@salesforce/storefront-next-runtime` package. This mirrors the
 * `ViewPageEvent.routeType` augmentation in `route-analytics.ts`.
 *
 * DEDUP. GA4 `purchase` MUST fire at most once per transaction, or revenue is double-counted
 * on any refresh/back-forward/redirect back to the confirmation page. SFRA guarded this with
 * `session.privacy.trackingOrderSubmitted`; the SF Next equivalent is a durable client marker
 * keyed by order number — see `hasPurchaseBeenTracked`/`markPurchaseTracked`.
 */
import type { BaseEvent } from '@salesforce/storefront-next-runtime/events';
import type { ShopperOrders } from '@/scapi';

/**
 * Analytics event fired once when a shopper reaches a confirmed order. Carries the full SCAPI
 * `Order` so the adapter can build the GA4 `purchase` ecommerce payload; the adapter — not the
 * producer — owns the GA4 field mapping, consistent with every other event in this system.
 */
export interface PurchaseEvent extends BaseEvent {
    eventType: 'purchase';
    order?: ShopperOrders.schemas['Order'];
    /**
     * Server-built parity payload for PDP native Apple Pay, whose SFCC-owned order is not readable
     * through the current SLAS session. It still travels through the normal adapter consent/toggle
     * path; only the GA4 data-layer adapter consumes it.
     */
    nativeDataLayerEvent?: Record<string, unknown>;
    nativeOrderNo?: string;
}

// Register `purchase` on the runtime's closed `AnalyticsEvent` union via the sanctioned
// `AnalyticsEventExtensions` escape hatch. Once merged, `createEvent('purchase', { order,
// payload })` type-checks, `purchase` becomes a required key of the `eventToggles` record,
// and the GA4 adapter's `switch (event.eventType)` can handle `'purchase'` type-safely.
declare module '@salesforce/storefront-next-runtime/events' {
    interface AnalyticsEventExtensions {
        PurchaseEvent: PurchaseEvent;
    }
}

/**
 * Storage key for the per-order dedup marker. Namespaced so it can't collide with other
 * `sessionStorage` usage.
 */
const purchaseTrackedKey = (orderNo: string): string => `ga4:purchase-tracked:${orderNo}`;

/**
 * Validate the analytics-only payload delivered by the same-origin native Apple Pay frame. The
 * redundant identifiers prevent one successful order handoff from attributing another order's
 * value, and the size cap bounds work before the object reaches an analytics adapter.
 */
export function validatedNativePurchase(value: unknown, orderNo: string): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const purchase = value as Record<string, unknown>;
    const ecommerce = purchase.ecommerce;
    if (!ecommerce || typeof ecommerce !== 'object' || Array.isArray(ecommerce)) return null;

    const transactionId = (ecommerce as Record<string, unknown>).transaction_id;
    if (
        purchase.event !== 'purchase' ||
        purchase['Order Number'] !== orderNo ||
        transactionId !== orderNo ||
        purchase.$event_id !== `orderConfirmation-${orderNo}`
    ) {
        return null;
    }

    try {
        return JSON.stringify(purchase).length <= 131072 ? purchase : null;
    } catch {
        return null;
    }
}

/**
 * Has the GA4 `purchase` for this order already been recorded in THIS browser session?
 *
 * Durable across refresh / back-forward / a redirect that re-lands on the confirmation page —
 * the SF Next equivalent of SFRA's `session.privacy.trackingOrderSubmitted`. `sessionStorage`
 * (not `localStorage`) is deliberate: the guard should last for the shopper's session but not
 * suppress a legitimately new session, and it is wiped when the tab closes. Guarded for SSR /
 * privacy-mode where `sessionStorage` may throw or be absent — on failure we report "not
 * tracked" so a storage error degrades to (at worst) a duplicate, never a silently dropped
 * conversion.
 */
export function hasPurchaseBeenTracked(orderNo: string): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return window.sessionStorage.getItem(purchaseTrackedKey(orderNo)) === '1';
    } catch {
        return false;
    }
}

/**
 * Record that the GA4 `purchase` for this order has fired, so a subsequent mount / refresh of
 * the confirmation page does not re-fire it. Best-effort: a `sessionStorage` failure is
 * swallowed (the event still fired; the only cost of a failed write is a possible duplicate).
 */
export function markPurchaseTracked(orderNo: string): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.setItem(purchaseTrackedKey(orderNo), '1');
    } catch {
        // ignore — see hasPurchaseBeenTracked for rationale
    }
}
